import type { ReactNode } from 'react';
import { Link } from 'react-router';
import { clsx } from '../../ds/clsx';
import { Banner, Button, Skeleton } from '../../ds/components';
import { ApiError, errorMessage } from '../../lib/api';
import type { TodayMetrics } from './api';
import styles from './ui.module.css';

/** Building blocks shared by the dialer screens (Terminierung A2). */

/** The crumb row above the content („Dialer · Gespräch läuft"); `end` sits on the right. */
export function Crumb({ children, end }: { children: ReactNode; end?: ReactNode }) {
  return (
    <div className={styles.crumbRow}>
      <nav className={styles.crumb} aria-label="Pfad">
        {children}
      </nav>
      {end ? <div className={styles.crumbEnd}>{end}</div> : null}
    </div>
  );
}

/** Page frame: crumb + the 14/32/38 content column with 16 px gaps. */
export function DialerPage({
  crumb,
  children,
  wide = false,
}: {
  crumb: ReactNode;
  children: ReactNode;
  wide?: boolean;
}) {
  return (
    <div className={clsx(styles.page, wide && styles.pageWide)}>
      {crumb}
      <div className={styles.content}>{children}</div>
    </div>
  );
}

/** Kennzahlenleiste (A2): five cells, a „hoch" cell (record) in the accent with a three-fold pulse. */
export function MetricsBar({
  metrics,
  loading = false,
}: {
  metrics: TodayMetrics | undefined;
  loading?: boolean;
}) {
  if (!metrics) {
    return (
      <div className={styles.metrics} aria-busy={loading || undefined}>
        {Array.from({ length: 5 }, (_, index) => (
          <div key={index} className={styles.metric}>
            <Skeleton width={120} height={11} />
            <div className={styles.metricValueRow}>
              <Skeleton width={56} height={28} />
            </div>
          </div>
        ))}
      </div>
    );
  }
  return (
    <div className={styles.metrics} role="group" aria-label="Kennzahlen heute">
      {metrics.cells.map((cell) => (
        <div key={cell.key} className={clsx(styles.metric, cell.high && styles.metricHigh)}>
          <div className={styles.metricLabel}>{cell.label}</div>
          <div className={styles.metricValueRow}>
            <span className={clsx(styles.metricValue, cell.high && styles.metricPulse)}>{cell.value}</span>
            {cell.sub ? <span className={styles.metricSub}>{cell.sub}</span> : null}
          </div>
        </div>
      ))}
    </div>
  );
}

/** Eyebrow text of the cards (2xs, uppercase, eyebrow tracking). */
export function Eyebrow({
  children,
  tone = 'subtle',
  className,
}: {
  children: ReactNode;
  tone?: 'subtle' | 'accent' | 'inverse';
  className?: string | undefined;
}) {
  return (
    <div
      className={clsx(
        styles.eyebrow,
        tone === 'accent' && styles.eyebrowAccent,
        tone === 'inverse' && styles.eyebrowInverse,
        className,
      )}
    >
      {children}
    </div>
  );
}

/** Label/value row of the cards („E-Mail … s.brunnthaler@gmx.de"). */
export function FactRow({
  label,
  value,
  inverse = false,
}: {
  label: ReactNode;
  value: ReactNode;
  inverse?: boolean;
}) {
  return (
    <div className={clsx(styles.factRow, inverse && styles.factRowInverse)}>
      <span className={styles.factLabel}>{label}</span>
      <span className={styles.factValue}>{value}</span>
    </div>
  );
}

/** Dialer-Liste switch of Terminierung 04 (34 × 19 track, „an"/„aus"). */
export function ListSwitch({
  checked,
  label,
  disabled = false,
  busy = false,
  onChange,
}: {
  checked: boolean;
  label: string;
  disabled?: boolean;
  busy?: boolean;
  onChange: (checked: boolean) => void;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      aria-busy={busy || undefined}
      disabled={disabled}
      className={styles.switchButton}
      onClick={() => onChange(!checked)}
    >
      <span className={clsx(styles.switchTrack, checked && styles.switchOn)} aria-hidden="true">
        <span className={styles.switchThumb} />
      </span>
      <span className={clsx(styles.switchLabel, checked && styles.switchLabelOn)}>
        {checked ? 'an' : 'aus'}
      </span>
    </button>
  );
}

/** Load error of a page (Deal-Pipeline 15 pattern): danger banner with „Erneut laden". */
export function LoadError({
  error,
  what,
  onRetry,
  retrying = false,
}: {
  error: unknown;
  what: string;
  onRetry: () => void;
  retrying?: boolean;
}) {
  const status = error instanceof ApiError ? error.status : null;
  return (
    <Banner
      tone="danger"
      actions={
        <Button variant="destructiveFilled" size="small" loading={retrying} onClick={onRetry}>
          Erneut laden
        </Button>
      }
    >
      {what} konnte nicht geladen werden{status !== null ? ` · Fehler ${status}` : ''}. {errorMessage(error)}
    </Banner>
  );
}

/** Understated text link of the screens (accent text, 35 % underline). */
export function InlineLink({ to, children }: { to: string; children: ReactNode }) {
  return (
    <Link to={to} className={styles.inlineLink}>
      {children}
    </Link>
  );
}

/** Figures of the facts rows of 01/03 („Wiedervorlagen heute 19"). */
export function Fact({
  label,
  value,
  display = true,
}: {
  label: string;
  value: ReactNode;
  display?: boolean;
}) {
  return (
    <div>
      <Eyebrow>{label}</Eyebrow>
      <div className={display ? styles.factFigure : styles.factText}>{value}</div>
    </div>
  );
}
